/**
 * Atmosphere on top of the Sky: biome sky / fog / water-fog colours blended
 * across borders, weather darkening (rain, thunder, lightning flash), the
 * darker fog below y 32, and the Nether's ambient particles (ash, white ash,
 * crimson / warped spores).
 *
 * Run `EnvAmbience.apply` every frame right after Sky.update() and
 * DimensionEnv.apply(); it only rewrites the shared env uniforms (and the sky
 * dome's sun / moon / star / cloud uniforms) for the overworld, and leaves
 * every other dimension alone. All colours here are authored in sRGB and held
 * as THREE.Color (linear).
 */

import * as THREE from 'three';
import type { Biome } from '../worldgen/biomes';
import { BIOME_LIST } from '../worldgen/biomes';
import {
  AMBIENT_COLOR, biomeFogColor, biomeSkyColor, biomeWaterFog, DEFAULT_FOG, DEFAULT_WATER_FOG, hasCloserWaterFog,
  NETHER_ATMOSPHERE, skyColorOfTemp, type AmbientKind,
} from '../worldgen/biomeClimate';
import type { NetherBiome } from '../worldgen/nether';
import type { DimensionId } from '../world/dimension';
import { weather, type Weather } from '../game/weather';
import type { EnvUniforms } from './env';

const NB = BIOME_LIST.length;
const hex = (h: number): THREE.Color => new THREE.Color(h);

const SKY: THREE.Color[] = Array.from({ length: NB }, (_, i) => hex(biomeSkyColor(i as Biome)));
const FOG: THREE.Color[] = Array.from({ length: NB }, (_, i) => hex(biomeFogColor(i as Biome)));
const WFOG: THREE.Color[] = Array.from({ length: NB }, (_, i) => hex(biomeWaterFog(i as Biome)));
const PLAINS_SKY = hex(skyColorOfTemp(0.8));
const PLAINS_FOG = hex(DEFAULT_FOG);
const BASE_WFOG = hex(DEFAULT_WATER_FOG);
/** Blend sample offsets (blocks): a 3x3 spread roughly the width of vanilla's blend. */
const BLEND = 8;
/** Seconds for the blended colours to settle (vanilla eases fog over a few seconds). */
const EASE = 1.2;
/** How strongly a biome's water fog departs from the default (the base underwater fog is brighter than 0x050533). */
const WATER_FOG_GAIN = 2;
/** Fog darkens below this y (vanilla: clear colour scale 1/32 over the build floor). */
const DARK_Y = 32;

const _lum = (c: THREE.Color): number => c.r * 0.3 + c.g * 0.59 + c.b * 0.11;

/** Vanilla rain / thunder desaturation: towards a darker grey by strength. */
function weatherGrey(c: THREE.Color, rain: number, thunder: number): void {
  if (rain > 0) {
    const g = _lum(c) * 0.6, k = 1 - rain * 0.75;
    c.r = c.r * k + g * (1 - k); c.g = c.g * k + g * (1 - k); c.b = c.b * k + g * (1 - k);
  }
  if (thunder > 0) {
    const g = _lum(c) * 0.2, k = 1 - thunder * 0.75;
    c.r = c.r * k + g * (1 - k); c.g = c.g * k + g * (1 - k); c.b = c.b * k + g * (1 - k);
  }
}

/** Dome / cloud uniforms found once on the Sky group (the sky.ts shader has no weather input). */
interface DomeHandles {
  sunDisc: THREE.Color;
  sunHalo: THREE.Color;
  stars: THREE.IUniform<number>;
  moon: THREE.IUniform<THREE.Color>;
  moonBase: THREE.Color;
  cloudTint: THREE.Color | null;
}

export class EnvAmbience {
  private readonly sky = new THREE.Color();
  private readonly fog = new THREE.Color();
  private readonly wfog = new THREE.Color().copy(WFOG[0] ?? BASE_WFOG);
  private closer = 0;
  private readonly tSky = new THREE.Color();
  private readonly tFog = new THREE.Color();
  private readonly tWfog = new THREE.Color();
  private tCloser = 0;
  private acc = 99;
  private primed = false;
  private readonly flashCol = new THREE.Color(0.8, 0.82, 1.0);
  private readonly dome: DomeHandles | null;

  constructor(
    private readonly env: EnvUniforms,
    skyGroup: THREE.Object3D,
    private readonly biomeAt: (x: number, z: number) => Biome,
    private readonly w: Weather = weather,
  ) {
    this.sky.copy(PLAINS_SKY); this.fog.copy(PLAINS_FOG);
    this.dome = findDome(skyGroup);
  }

  /** Re-sample the biome blend (a 3x3 of columns) into the target colours. */
  private sample(px: number, pz: number): void {
    this.tSky.setRGB(0, 0, 0); this.tFog.setRGB(0, 0, 0); this.tWfog.setRGB(0, 0, 0);
    let closer = 0;
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const b = this.biomeAt(Math.floor(px + dx * BLEND), Math.floor(pz + dz * BLEND));
        this.tSky.add(SKY[b] ?? PLAINS_SKY); this.tFog.add(FOG[b] ?? PLAINS_FOG); this.tWfog.add(WFOG[b] ?? BASE_WFOG);
        if (hasCloserWaterFog(b)) closer++;
      }
    }
    this.tSky.multiplyScalar(1 / 9); this.tFog.multiplyScalar(1 / 9); this.tWfog.multiplyScalar(1 / 9);
    this.tCloser = closer / 9;
  }

  /**
   * Apply after Sky.update / DimensionEnv.apply. `fogFar` is the base fog
   * end (unused underwater, where the view distance is the sky's own).
   */
  apply(dim: DimensionId, camera: THREE.Vector3, dt: number, underwater: boolean, inLava: boolean): void {
    if (dim !== 'overworld') return;
    const env = this.env, w = this.w;
    this.acc += dt;
    if (this.acc >= 0.25) { this.acc = 0; this.sample(camera.x, camera.z); }
    if (!this.primed) {
      this.primed = true;
      this.sky.copy(this.tSky); this.fog.copy(this.tFog); this.wfog.copy(this.tWfog); this.closer = this.tCloser;
    } else {
      const k = 1 - Math.exp(-dt / EASE);
      this.sky.lerp(this.tSky, k); this.fog.lerp(this.tFog, k); this.wfog.lerp(this.tWfog, k);
      this.closer += (this.tCloser - this.closer) * k;
    }

    const day = env.uDayLight.value;
    const rain = w.rainStrength(), thunder = w.thunderStrength();

    if (underwater && !inLava) {
      // biome water fog as an offset from the default, plus swamp-style closer fog
      const f = env.uFogColor.value;
      f.r = Math.max(0, f.r + (this.wfog.r - BASE_WFOG.r) * WATER_FOG_GAIN * (0.12 + 0.88 * day));
      f.g = Math.max(0, f.g + (this.wfog.g - BASE_WFOG.g) * WATER_FOG_GAIN * (0.12 + 0.88 * day));
      f.b = Math.max(0, f.b + (this.wfog.b - BASE_WFOG.b) * WATER_FOG_GAIN * (0.12 + 0.88 * day));
      env.uFogFar.value *= 1 - 0.15 * this.closer;
    } else if (!inLava) {
      // sky and fog: biome colour relative to plains, only by day (night is the sky's own)
      tintRatio(env.uZenith.value, this.sky, PLAINS_SKY, day);
      tintRatio(env.uHorizon.value, this.fog, PLAINS_FOG, day);
      tintRatio(env.uFogColor.value, this.fog, PLAINS_FOG, day);
      // caves: the clear colour fades to black towards the build floor
      const dark = Math.min(1, Math.max(0, camera.y / DARK_Y));
      if (dark < 1) {
        env.uZenith.value.multiplyScalar(dark); env.uHorizon.value.multiplyScalar(dark); env.uFogColor.value.multiplyScalar(dark);
      }
      if (rain > 0) {
        weatherGrey(env.uZenith.value, rain, thunder);
        weatherGrey(env.uHorizon.value, rain, thunder);
        weatherGrey(env.uFogColor.value, rain, thunder);
        // the downpour closes the view in a little
        const near = 1 - 0.18 * rain - 0.1 * thunder;
        env.uFogFar.value *= near; env.uFogNear.value *= near * near;
      }
      if (w.flash > 0) {
        const f = w.flash * w.flash * 0.7;
        env.uZenith.value.lerp(this.flashCol, f); env.uHorizon.value.lerp(this.flashCol, f); env.uFogColor.value.lerp(this.flashCol, f);
      }
    }

    if (rain > 0) {
      // overcast: direct light all but gone (no hard shadows), ambient and sky light down by vanilla's skyDarken
      env.uSunColor.value.multiplyScalar(1 - 0.88 * rain);
      env.uAmbientColor.value.multiplyScalar(1 - 0.3125 * rain);
      env.uDayLight.value = day * w.skyBrightness();
    }
    if (w.flash > 0) env.uAmbientColor.value.lerp(this.flashCol, w.flash * w.flash * 0.5);

    const d = this.dome;
    if (d) {
      const clear = 1 - rain;
      d.sunDisc.multiplyScalar(clear); d.sunHalo.multiplyScalar(clear);
      d.stars.value *= clear;
      d.moon.value.copy(d.moonBase).multiplyScalar(clear);
      if (d.cloudTint && rain > 0) d.cloudTint.multiplyScalar(1 - 0.55 * rain - 0.2 * thunder);
    }
  }
}

/** c *= lerp(1, biome / plains, k) per channel, with the ratio kept sane. */
function tintRatio(c: THREE.Color, biome: THREE.Color, base: THREE.Color, k: number): void {
  const r = (b: number, p: number): number => 1 + (Math.min(3, Math.max(0.3, b / Math.max(p, 1e-3))) - 1) * k;
  c.r *= r(biome.r, base.r); c.g *= r(biome.g, base.g); c.b *= r(biome.b, base.b);
}

function findDome(group: THREE.Object3D): DomeHandles | null {
  let dome: DomeHandles | null = null;
  let cloudTint: THREE.Color | null = null;
  group.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.ShaderMaterial | undefined;
    const u = m?.uniforms;
    if (!u) return;
    if (u.uSunDisc && u.uStars && u.uMoonColor) {
      // swap in a private moon colour: sky.ts' constant is shared and must not be scaled in place
      const base = (u.uMoonColor.value as THREE.Color).clone();
      u.uMoonColor = { value: base.clone() };
      dome = {
        sunDisc: u.uSunDisc.value as THREE.Color, sunHalo: u.uSunHalo.value as THREE.Color,
        stars: u.uStars as THREE.IUniform<number>, moon: u.uMoonColor as THREE.IUniform<THREE.Color>,
        moonBase: base, cloudTint: null,
      };
    } else if (u.uTint) {
      cloudTint = u.uTint.value as THREE.Color;
    }
  });
  if (dome) (dome as DomeHandles).cloudTint = cloudTint;
  return dome;
}

// --- Nether ambient particles ----------------------------------------------

const POOL = 640;
const BOX = 22;
const LIFE_MIN = 5, LIFE_MAX = 9;

/**
 * The Nether's floating ash and spores around the camera: one THREE.Points,
 * a fixed pool recycled round-robin, spawn rate set by the biome's config.
 */
export class NetherParticles {
  readonly points: THREE.Points;
  private readonly pos = new Float32Array(POOL * 3);
  private readonly col = new Float32Array(POOL * 3);
  private readonly vel = new Float32Array(POOL * 3);
  private readonly age = new Float32Array(POOL).fill(1e9);
  private readonly life = new Float32Array(POOL).fill(1);
  private cursor = 0;
  private acc = 0;
  private rnd = 987654321;
  private readonly geom = new THREE.BufferGeometry();
  /** Kind spawned right now (the camera's biome); exposed for tests. */
  kind: AmbientKind = 'none';

  constructor() {
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geom.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.points = new THREE.Points(this.geom, new THREE.PointsMaterial({
      size: 0.085, sizeAttenuation: true, vertexColors: true, transparent: true, opacity: 0.9, depthWrite: false, fog: false,
    }));
    this.points.frustumCulled = false;
    this.points.visible = false;
    for (let i = 0; i < POOL; i++) this.pos[i * 3 + 1] = -1000;
  }

  private rand(): number {
    this.rnd = (Math.imul(this.rnd, 1664525) + 1013904223) >>> 0;
    return this.rnd / 4294967296;
  }

  /** `biome` is the Nether biome under the camera, or undefined outside the Nether. */
  update(dt: number, cam: THREE.Vector3, dim: DimensionId, biome: NetherBiome | undefined): void {
    const cfg = dim === 'nether' && biome !== undefined ? NETHER_ATMOSPHERE[biome] : undefined;
    this.kind = cfg ? cfg.particle : 'none';
    let alive = false;
    if (cfg && cfg.rate > 0) {
      this.acc += dt * cfg.rate;
      const c = AMBIENT_COLOR[cfg.particle];
      while (this.acc >= 1) {
        this.acc -= 1;
        const i = this.cursor;
        this.cursor = (i + 1) % POOL;
        this.pos[i * 3] = cam.x + (this.rand() * 2 - 1) * BOX;
        this.pos[i * 3 + 1] = cam.y + (this.rand() * 2 - 1) * BOX * 0.6;
        this.pos[i * 3 + 2] = cam.z + (this.rand() * 2 - 1) * BOX;
        const falling = cfg.particle === 'ash' || cfg.particle === 'white_ash';
        // ash drifts down on a faint wind; spores hang and wander upward
        this.vel[i * 3] = (this.rand() - 0.5) * 0.5 + (falling ? 0.35 : 0);
        this.vel[i * 3 + 1] = falling ? -0.25 - this.rand() * 0.2 : 0.05 + this.rand() * 0.15;
        this.vel[i * 3 + 2] = (this.rand() - 0.5) * 0.5;
        const shade = 0.75 + this.rand() * 0.25;
        this.col[i * 3] = c[0] * shade; this.col[i * 3 + 1] = c[1] * shade; this.col[i * 3 + 2] = c[2] * shade;
        this.age[i] = 0;
        this.life[i] = LIFE_MIN + this.rand() * (LIFE_MAX - LIFE_MIN);
      }
    }
    for (let i = 0; i < POOL; i++) {
      if (this.age[i] >= this.life[i]) { if (this.pos[i * 3 + 1] > -999) this.pos[i * 3 + 1] = -1000; continue; }
      alive = true;
      this.age[i] += dt;
      this.pos[i * 3] += this.vel[i * 3] * dt;
      this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt;
      this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
    }
    this.points.visible = alive;
    if (alive) {
      (this.geom.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      (this.geom.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    }
  }
}
